import { getOrderDetails } from '@app/ducks/order';
import { DELETE_SELECTIONS_MUTATION_KEY } from '@app/services/hooks/orders/useDeleteSelections';
import { cn } from '@app/utils/cn';
import { useUrlProductId } from '@app/utils/hooks';
import { useUrlVariantId } from '@app/utils/hooks/useUrlVariantId';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { useIsMutating } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { OrderBottomBar } from '../orders/OrderBottomBar';
import { OrderTopBar } from '../orders/OrderTopBar';
import DefaultLoader from '../various/loaders/DefaultLoader';
import { PageTitle } from '../various/PageTitle';

import { AllProductsContextProvider } from './context/AllProductsContext';
import { AllProductsPageContent } from './AllProductsPageContent';

export const AllProductsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['products', 'selections']);
  const isDeletingAnySelection = useIsMutating({ mutationKey: [DELETE_SELECTIONS_MUTATION_KEY] }) > 0;
  const orderDetails = useSelector(getOrderDetails);
  const productId = useUrlProductId();
  const variantId = useUrlVariantId();
  const hasProductId = isDefined(productId) && !isEmpty(productId);
  const title = React.useMemo(() => {
    if (!isDefined(orderDetails.order.orderNumber)) {
      return t('products:product_other');
    }

    return `${t('selections:selection_number', { orderNumber: orderDetails.order.orderNumber })} - ${t('products:product_other')}`;
  }, [orderDetails.order.orderNumber, t]);

  return (
    <PageTitle title={title}>
      {isDeletingAnySelection && <DefaultLoader overlay />}
      <OrderTopBar isAllProductsPage />
      <OrderBottomBar
        orderId={id}
        location="products"
        className={cn('transition-all', 'px-4 md:pl-6 lg:pl-[max(2rem,calc((100%-1280px)/2))] lg:pr-[max(2rem,calc((100%-1280px)/2))]', {
          'lg:pl-[max(19rem,calc((100%-1280px)/2+16rem))] lg:pr-[max(2rem,calc((100%-1280px)/2))]': !hasProductId,
        })}
      />
      <AllProductsContextProvider orderId={id!}>
        <AllProductsPageContent productId={productId} variantId={variantId} />
      </AllProductsContextProvider>
    </PageTitle>
  );
};
